
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { VinLookup } from "@/components/vin/VinLookup";
import { VinLookupHistory } from "@/components/vin/VinLookupHistory";
import { useVinLookup } from "@/hooks/useVinLookup";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdasAnalysisDebugger } from "@/components/analysis/AdasAnalysisDebugger";
import { useEstimateAnalysis } from "@/hooks/useEstimateAnalysis";
import { Button } from "@/components/ui/button";
import { FileSearch, Info } from "lucide-react";
import { toast } from "sonner";
import { interpretStyleFlags } from "@/utils/chromeDataUtils";

const Analysis = () => {
  const { lookupHistory, deleteLookup, currentResults } = useVinLookup();
  const [selectedVin, setSelectedVin] = useState<string | null>(null);
  const [estimateText, setEstimateText] = useState<string>("");
  const { analyzeEstimate, isAnalyzing, analysisResult, debugData } = useEstimateAnalysis();

  // Add type assertion to make the compiler happy
  const lookupHistoryWithRequired = lookupHistory.map(history => ({
    ...history,
    raw_dataone_response: history.raw_dataone_response || null
  }));

  // Extract all possible ADAS features from current results
  const extractAdasFeatures = () => {
    if (!currentResults) return null;
    
    // Log the current results to help with debugging
    console.log("Current results:", currentResults);
    
    // Try different paths to locate ADAS features
    let adasFeatures: any = null;
    let formattedFeatures: Record<string, any> = {};
    
    // Path 1: dataOneData.adas_features
    if (currentResults.dataOneData?.adas_features) {
      console.log("Found ADAS features at dataOneData.adas_features");
      adasFeatures = currentResults.dataOneData.adas_features;
    }
    // Path 2: dataOneData.vehicles[0].adas_features
    else if (currentResults.dataOneData?.vehicles?.[0]?.adas_features) {
      console.log("Found ADAS features at dataOneData.vehicles[0].adas_features");
      adasFeatures = currentResults.dataOneData.vehicles[0].adas_features;
    }
    // Path 3: From NHTSA data for safety features
    else if (currentResults.nhtsaData) {
      console.log("No DataOne ADAS features found, using NHTSA safety data");
      
      // Extract safety features from NHTSA data
      const nhtsaData = currentResults.nhtsaData;
      const safetyFields = [
        "ABS", "ESC", "Traction", "BackupCam", "ParkAssist",
        "ForwardCollisionWarning", "LaneDepartureWarning", "LaneKeepSystem",
        "BlindSpotMon", "CIB", "PedestrianAEB", "RearCrossTrafficMonitor"
      ];
      
      safetyFields.forEach(field => {
        const value = nhtsaData[field as keyof typeof nhtsaData];
        if (value && value !== "Not Applicable" && value !== "N/A") {
          const normalizedValue = value.toLowerCase();
          let confidence = "Maybe Installed";
          
          if (normalizedValue.includes("standard")) {
            confidence = "Installed";
          }
          
          formattedFeatures[field] = {
            name: field,
            value,
            confidence,
            installed: confidence === "Installed",
            maybeInstalled: confidence === "Maybe Installed"
          };
        }
      });
      
      if (Object.keys(formattedFeatures).length > 0) {
        console.log("Extracted NHTSA safety features:", Object.keys(formattedFeatures).length);
        return formattedFeatures;
      }
    }
    
    // If we found raw ADAS features but haven't formatted them yet, do it now
    if (adasFeatures && Object.keys(formattedFeatures).length === 0) {
      // Convert raw ADAS features to consistent format
      Object.entries(adasFeatures).forEach(([key, value]: [string, any]) => {
        formattedFeatures[key] = {
          name: key,
          confidence: value.installed ? "Installed" : "Maybe Installed",
          installed: value.installed || false,
          maybeInstalled: !value.installed,
          details: value
        };
      });
      
      return formattedFeatures;
    }
    
    return Object.keys(formattedFeatures).length > 0 ? formattedFeatures : adasFeatures;
  };
  
  // Get ADAS features
  const adasFeatures = extractAdasFeatures();
  
  // Log ADAS features when they change
  useEffect(() => {
    if (adasFeatures) {
      console.log("ADAS features available for analysis:", 
                  typeof adasFeatures === 'object' ? Object.keys(adasFeatures).length : 'unknown format');
      console.log("ADAS features:", adasFeatures);
    } else {
      console.log("No ADAS features found");
    }
  }, [adasFeatures]);

  const handleRunTest = async () => {
    if (estimateText) {
      if (!adasFeatures) {
        toast.warning("No ADAS features found in current results. Analysis may be limited.", {
          description: "Try looking up a VIN first to get ADAS features for better results."
        });
      }
      await analyzeEstimate(estimateText, adasFeatures);
    }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="vehicle-lookup" className="space-y-6">
        <TabsList>
          <TabsTrigger value="vehicle-lookup">Vehicle Lookup</TabsTrigger>
          <TabsTrigger value="adas-testing">ADAS Analysis Testing</TabsTrigger>
        </TabsList>
        
        <TabsContent value="vehicle-lookup">
          <Card>
            <CardHeader>
              <CardTitle>Vehicle Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <VinLookup />
            </CardContent>
          </Card>
          
          <VinLookupHistory lookups={lookupHistoryWithRequired} onDelete={deleteLookup} />
        </TabsContent>
        
        <TabsContent value="adas-testing">
          <Card>
            <CardHeader>
              <CardTitle>ADAS Analysis Testing</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium mb-2">1. Select a VIN or use current results</h3>
                  <div className="text-sm text-muted-foreground mb-4">
                    {currentResults ? 
                      <div className="flex items-center">
                        <span>Using current results: {currentResults.nhtsaData.VIN}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="ml-2" 
                          onClick={() => {
                            console.log("Current results:", currentResults);
                            toast.info("Current results logged to console");
                          }}
                        >
                          <Info className="h-4 w-4" />
                        </Button>
                      </div> : 
                      'No current results. Run a VIN lookup first.'}
                  </div>
                  
                  <h3 className="text-sm font-medium mb-2">2. Enter Estimate Text (or use existing data)</h3>
                  <textarea 
                    className="w-full h-40 p-2 border rounded-md"
                    value={estimateText}
                    onChange={(e) => setEstimateText(e.target.value)}
                    placeholder="Paste repair estimate text here..."
                  />
                  
                  <h3 className="text-sm font-medium mt-4 mb-2">3. ADAS Features</h3>
                  <div className="flex items-start space-x-2 text-sm text-muted-foreground mb-4">
                    <div>
                      {adasFeatures ? 
                        <div>
                          <p>ADAS features found: {typeof adasFeatures === 'object' ? Object.keys(adasFeatures).length : 'Unknown format'}</p>
                          <div className="text-xs mt-1">
                            <p>Features include:</p>
                            <ul className="list-disc pl-4 mt-1">
                              {typeof adasFeatures === 'object' && Object.entries(adasFeatures).slice(0, 5).map(([key, value]: [string, any]) => (
                                <li key={key}>
                                  {key}: {value.confidence || (value.installed ? 'Installed' : 'Maybe Installed')}
                                </li>
                              ))}
                              {typeof adasFeatures === 'object' && Object.keys(adasFeatures).length > 5 && 
                                <li>...and {Object.keys(adasFeatures).length - 5} more</li>
                              }
                            </ul>
                          </div>
                        </div> : 
                        <div>
                          <p>No ADAS features detected in current results</p>
                          <p className="text-xs">Check DataOne data structure to see where ADAS features are stored</p>
                        </div>
                      }
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        console.log("ADAS features:", adasFeatures);
                        if (currentResults?.dataOneData) {
                          console.log("Full DataOne data:", currentResults.dataOneData);
                        }
                        toast.info("ADAS features and DataOne data logged to console");
                      }}
                    >
                      <Info className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <Button 
                    className="mt-4"
                    onClick={handleRunTest}
                    disabled={!estimateText || isAnalyzing}
                  >
                    <FileSearch className="mr-2 h-4 w-4" />
                    Run Analysis Test
                  </Button>
                </div>
                
                {(debugData || isAnalyzing) && (
                  <AdasAnalysisDebugger 
                    isLoading={isAnalyzing}
                    requestData={debugData?.requestData}
                    responseData={debugData?.responseData}
                    onRetry={handleRunTest}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analysis;
